import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import {
  Card,
  CardHeader,
  EmptyState,
  FilterBar,
  InlineStats,
  Skeleton
} from '../../components/admin/ui';
import { useToast } from '../../components/admin/overlays';
import { formatNumber } from '../../components/admin/labels';
import { IconSearch } from '../../components/admin/icons';
import { UpgradeGate, WindowPicker } from '../../components/courier/ui';
import { useCourierSession } from '../../components/courier/CourierLayout';

/**
 * Postcode areas generating the most reports.
 *
 * Outward codes only ("SW1A", not "SW1A 1AA") — a full postcode is a
 * household, and nothing here is about an individual delivery or person.
 * Shown as a ranked grid of tiles rather than a table: the comparison that
 * matters is between areas, and sized tiles make that immediate.
 */
const CourierHotspots = () => {
  const toast = useToast();
  const { me, fullAccess } = useCourierSession() || {};

  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!fullAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: payload } = await api.get(`/api/courier-portal/hotspots?days=${days}`);
      setData(payload);
    } catch (error) {
      if (error.response?.data?.code !== 'UPGRADE_REQUIRED') {
        toast('Could not load postcode hotspots.', { tone: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, [days, fullAccess, toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (!fullAccess) {
    return (
      <UpgradeGate
        title="Find the postcode areas driving your reports"
        description="Problems concentrate geographically. Intelligence ranks the outward codes generating the most reports against your network, and what goes wrong in each."
        features={me?.pricing?.courierPlanPrices?.intelligence?.features || []}
        price={me?.pricing?.courierPlanPrices?.intelligence?.price ?? null}
      />
    );
  }

  const spots = data?.hotspots || [];
  const total = spots.reduce((sum, s) => sum + s.cases, 0);
  const max = Math.max(...spots.map((s) => s.cases), 1);

  return (
    <div className="space-y-5">
      <Card>
        <FilterBar
          actions={
            <>
              <InlineStats
                items={[
                  { label: 'Areas', value: loading ? '—' : formatNumber(spots.length) },
                  { label: 'Reports', value: loading ? '—' : formatNumber(total) }
                ]}
              />
              <WindowPicker days={days} onChange={setDays} onRefresh={load} loading={loading} />
            </>
          }
        />

        {loading ? (
          <div className="grid gap-3 p-5 sm:grid-cols-3 lg:grid-cols-4">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((k) => (
              <Skeleton key={k} className="h-24 w-full" />
            ))}
          </div>
        ) : spots.length === 0 ? (
          <EmptyState
            icon={IconSearch}
            title="No postcode activity for this period"
            description={`Nothing has been reported against your network in the last ${days} days, or no reports carried a postcode.`}
          />
        ) : (
          <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-4">
            {spots.map((spot, i) => {
              // The bar is relative to the busiest area, so the leader is
              // always full-width and the rest read against it.
              const pct = Math.round((spot.cases / max) * 100);
              return (
                <div
                  key={spot.code}
                  className="rounded-xl border border-ink-200 bg-white p-4 transition-shadow hover:shadow-card"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-display text-lg font-800 tracking-tight text-ink-900">
                      {spot.code}
                    </span>
                    <span className="text-xs tabular-nums text-ink-500">#{i + 1}</span>
                  </div>
                  <p className="mt-1 font-display text-2xl font-800 tabular-nums text-info-700">
                    {formatNumber(spot.cases)}
                  </p>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-ink-100">
                    <div className="h-full rounded-full bg-info-500" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="mt-2 truncate text-[11px] text-ink-500" title={spot.topIssue || ''}>
                    {spot.topIssue || '—'}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card>
        <CardHeader
          title="How to read this"
          description="Outward codes only — the first half of a postcode, which covers a district rather than a household."
        />
      </Card>
    </div>
  );
};

export default CourierHotspots;
